import { useMutation } from 'react-query';
import axios, { AxiosError } from 'axios';
import { TerminalAppearanceAPI } from '../../../constants/API';
import { queryClient } from '../../../queryClient';
import { multipartHeaders } from '../../../constants/HeadersAPI';
import { useContext } from 'react';
import { AlertContext } from '../../../context/AlertProvider';
import { AlertType } from '../../../types/enums';
import { defaultSuccessMessage } from '../../../constants/alertText';
import { ErrorResponseDataType } from '../../../types/Admin/CategoriesTopicsTypes';
import { defaultErrorText } from '../../../types/Error/ErrorResponseType';

const FILE_FORMAT_ERROR_CODE = 106;
const unsupportedFormatMessage = 'Неподдерживаемый формат файла';
const connectionInterruptedMessage = 'Обрыв соединения. Файл не сохранён.';

const isUploadNetworkError = (error: AxiosError): boolean =>
  !error.response ||
  error.code === 'ERR_NETWORK' ||
  error.code === 'ECONNABORTED' ||
  error.message === 'Network Error';

const resolveChildrenBackgroundUploadError = (
  error: AxiosError<ErrorResponseDataType>
): string => {
  if (isUploadNetworkError(error)) {
    return connectionInterruptedMessage;
  }

  if (error.response?.data?.errorCode === FILE_FORMAT_ERROR_CODE) {
    return error.response.data.message || unsupportedFormatMessage;
  }

  return error.response?.data?.message || defaultErrorText;
};

export const usePostTerminalChildrenBackground = () => {
  const context = useContext(AlertContext);

  const {
    mutate: uploadTerminalChildrenBackground,
    isLoading: isUploadTerminalChildrenBackgroundLoading
  } = useMutation(
    ['TerminalAppearanceUploadChildrenBackground'],
    (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return axios.post(TerminalAppearanceAPI.uploadChildrenBackground, formData, multipartHeaders());
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries('TerminalAppearance')
          .then(() =>
            context?.setAlert({ type: AlertType.Success, message: defaultSuccessMessage })
          );
      },
      onError: (error: AxiosError<ErrorResponseDataType>): void => {
        if (error instanceof AxiosError) {
          context?.setAlert({
            type: AlertType.Error,
            message: resolveChildrenBackgroundUploadError(error)
          });
          return;
        }

        context?.setAlert({ type: AlertType.Error, message: defaultErrorText });
      }
    }
  );

  return { uploadTerminalChildrenBackground, isUploadTerminalChildrenBackgroundLoading };
};
